import {Auditing} from './auditing.model';
import {Attachment} from './attachment.model';
import {BaseTask} from './base-task.model';
import {DocumentGenerationEntry} from './document-generation-entry.model';
import {CustomField, CustomFieldOption, isSmartFieldType} from './custom-field.model';
import {CaseStatus} from '@ee/common/enums';
import {CustomFieldType, RenderSeparator} from '@ee/common/enums';
import {SubjectType} from '@ee/common/enums';
import {PublicationStatus} from '@ee/common/enums';

/**
 * A single entry in a {@link CaseTemplate} field registry — the source of truth
 * for a case type. Projections (intake, sidebar, documents, import) reference
 * fields by their immutable {@link key}. Mirrors the backend `FieldDefinition`.
 */
export interface FieldDefinition {
  key: string;
  label?: string;
  type?: CustomFieldType;
  options?: string[];
  /**
   * MULTI_SELECT only — the {value, label} pairs behind `options`. Optional and additive: absent
   * means each `options` entry is an option whose label equals its value (Req 2.2). `options` stays
   * equal to the ordered list of these values, so every existing reader of it is unaffected.
   */
  option_definitions?: CustomFieldOption[];
  /** MULTI_SELECT only — how selections join into one string in documents/reports. Null ⇒ COMMA. */
  render_separator?: RenderSeparator | null;
  /**
   * The value a control is pre-filled with on a form for a **new** case. Absent ⇒ no default. For a
   * DATE field it is the `TODAY` or `BLANK` sentinel or an ISO date; validated server-side against the
   * field's type and options by `FieldRegistryValidator`.
   */
  default_value?: any;
  required?: boolean;
  built_in?: boolean;
  removable?: boolean;
  hidden?: boolean;
  calculated?: boolean;
  calculated_expression?: string;
  /** True for currency fields — the UI renders them as `$X,XXX.XX`. */
  monetary?: boolean;
  /** When true, this field's amount(s) sum into the case claim total (DECIMAL scalar or RENT_HISTORY months). */
  include_in_total?: boolean;
  group?: string;
  help_text?: string;
  /** Expanded hint shown in a tooltip when the hint is long (modal "Expanded Hint"). */
  help_more_info?: string;
  /** What the field contains; shown in document-template field lists. */
  description?: string;
  /** Example populated value, shown in document-template field lists. */
  example?: string;
  validation?: Record<string, any>;
  subject_collection?: boolean;
  /** Party kind for a subject collection (absent ⇒ INDIVIDUAL). */
  subject_type?: SubjectType;
  /** Singular label for one subject (e.g. "Tenant", "Defendant"). */
  subject_label_singular?: string;
  /** Nested sub-field schema for each subject (one level deep). */
  subject_fields?: FieldDefinition[];
  /** Provenance: the org PartySchema this collection's sub-fields were copied from (enables refresh). */
  subject_schema_id?: string;
}

/**
 * Built-in case fields that hold money and should render as currency (`$X,XXX.XX`). Built-in fields are
 * form-derived in the case views (not from the registry), so they don't carry the backend `monetary` flag —
 * this key set is the source of truth for the UI. `interest_rate` is intentionally excluded (it's a %).
 */
export const MONETARY_CASE_FIELD_KEYS = new Set<string>([
  'past_due_rent', 'late_fees', 'damages', 'costs', 'court_costs', 'civil_recovery_fees',
  'attorney_fee', 'judgement_amount', 'lease_rent_amount', 'security_deposit', 'partial_payments'
]);

/**
 * Maps a registry {@link FieldDefinition} into the `CustomField` shape the shared
 * field-editor modal consumes (for editing an existing field).
 */
export function fieldDefinitionToCustomField(fd: FieldDefinition): CustomField {
  return {
    label: fd.label || fd.key,
    field: fd.key,
    type: fd.type || CustomFieldType.TEXT,
    options: fd.options,
    // Only for a type that has them, so no other field gains the two new properties (Req 1.4).
    ...(fd.option_definitions?.length ? {option_definitions: fd.option_definitions} : {}),
    ...(fd.render_separator ? {render_separator: fd.render_separator} : {}),
    // A registry field has no `mask`: the backend `FieldDefinition` carries none, and the PM case form
    // is SurveyJS-rendered. Masks are a `CustomField` (org custom field / attorney intake) concept.
    ...(fd.default_value !== null && fd.default_value !== undefined ? {default_value: fd.default_value} : {}),
    required: fd.required,
    value: fd.calculated_expression,
    hint: fd.help_text,
    hint_more_info: fd.help_more_info,
    description: fd.description,
    example: fd.example,
    hide: fd.hidden,
    include_in_total: fd.include_in_total,
    prevent_removal: fd.removable === false,
    built_in_field: fd.built_in
  };
}

/**
 * Maps the shared modal's `CustomField` result back into a registry
 * {@link FieldDefinition}, preserving immutable attributes (key/built_in/removable)
 * of the [existing] definition when editing.
 */
export function customFieldToFieldDefinition(cf: CustomField, existing?: FieldDefinition): FieldDefinition {
  const calculated = isSmartFieldType(cf.type);
  const definition: FieldDefinition = {
    ...(existing || {}),
    key: existing?.key ?? cf.field ?? '',
    label: cf.label,
    type: cf.type,
    options: cf.options,
    required: cf.required,
    hidden: cf.hide,
    removable: cf.prevent_removal ? false : (existing?.removable ?? true),
    built_in: existing?.built_in ?? cf.built_in_field ?? false,
    calculated,
    calculated_expression: calculated ? cf.value : existing?.calculated_expression,
    include_in_total: cf.include_in_total,
    help_text: cf.hint,
    help_more_info: cf.hint_more_info,
    description: cf.description,
    example: cf.example
  };
  // Set only for a type that has them, and cleared when a field is retyped away from MULTI_SELECT —
  // otherwise the spread of `existing` above would keep stale definitions on a plain text field, and
  // every other field would gain the two new properties on its next save (Req 1.4).
  if (cf.option_definitions?.length) {
    definition.option_definitions = cf.option_definitions;
  } else {
    delete definition.option_definitions;
  }
  if (cf.render_separator) {
    definition.render_separator = cf.render_separator;
  } else {
    delete definition.render_separator;
  }
  // Same treatment, and for the same reason: without the delete, the `existing` spread would keep a
  // default the user has just cleared, and one retyped into a field type the server rejects would fail
  // the whole template save with `default.invalid`.
  if (cf.default_value !== null && cf.default_value !== undefined && cf.default_value !== '') {
    definition.default_value = cf.default_value;
  } else {
    delete definition.default_value;
  }
  return definition;
}

/**
 * Builds the SurveyJS question JSON for a registry field, so the intake form
 * builder can offer registry fields as toolbox items (the field list is the
 * source of truth; the form is a layout over it).
 */
export function fieldDefinitionToSurveyQuestion(fd: FieldDefinition): Record<string, any> {
  // A subject collection renders as a SurveyJS paneldynamic carrying its nested schema.
  if (fd.subject_collection) {
    const singular = fd.subject_label_singular || 'Subject';
    return {
      type: 'paneldynamic',
      name: fd.key,
      title: fd.label || fd.key,
      subjectType: fd.subject_type || SubjectType.INDIVIDUAL,
      panelCount: 1,
      minPanelCount: 1,
      confirmDelete: true,
      panelAddText: `Add ${singular}`,
      panelRemoveText: 'Remove',
      renderMode: 'tab',
      templateTabTitle: `New ${singular}`,
      // ALL sub-fields render; hidden (case-only) ones as visible:false — the canvas shows them
      // ghosted and toggles visibility natively (round-tripped to the registry on save), and the
      // intake runtime never asks them. Post-intake they remain editable via the sidebar.
      // clearIfInvisible 'none' is scoped to just the hidden question — a survey-wide override
      // would also stop clearing unrelated visibleIf-conditional fields (e.g. eviction_reason_other).
      templateElements: (fd.subject_fields || []).map(sf => ({
        ...fieldDefinitionToSurveyQuestion(sf),
        ...(sf.hidden ? {visible: false, clearIfInvisible: 'none'} : {})
      }))
    };
  }
  // A RENT_HISTORY field renders as a paneldynamic of {month, year, amount} rows (up to 10).
  if (fd.type === CustomFieldType.RENT_HISTORY) {
    return rentHistorySurveyQuestion(fd);
  }
  const q: Record<string, any> = {
    type: 'text',
    name: fd.key,
    title: fd.label || fd.key
  };
  if (fd.required) {
    q.isRequired = true;
  }
  if (fd.help_text) {
    q.description = fd.help_text;
  }
  switch (fd.type) {
    case CustomFieldType.MULTI_TEXT:
      q.type = 'comment';
      break;
    case CustomFieldType.SELECT:
      q.type = 'dropdown';
      q.choices = fd.options || [];
      break;
    case CustomFieldType.RADIO:
      q.type = 'radiogroup';
      q.choices = fd.options || [];
      break;
    case CustomFieldType.TAGS:
      q.type = 'tagbox';
      q.choices = fd.options || [];
      break;
    case CustomFieldType.MULTI_SELECT:
      // Choices carry both halves so the canvas ticks the Display_Label and stores the
      // Stored_Value; with no definitions, label equals value as it does for every other
      // choice type (Req 2.2, 5.6).
      q.type = 'checkbox';
      q.choices = fd.option_definitions?.length
        ? fd.option_definitions.map(o => ({value: o.value, text: o.label}))
        : (fd.options || []);
      break;
    case CustomFieldType.BOOLEAN:
      q.type = 'boolean';
      break;
    case CustomFieldType.DATE:
      q.type = 'text';
      q.inputType = 'date';
      break;
    case CustomFieldType.INTEGER:
    case CustomFieldType.DECIMAL:
      q.type = 'text';
      q.inputType = 'number';
      break;
    case CustomFieldType.CALCULATED:
    case CustomFieldType.DATE_OFFSET:
      q.type = 'expression';
      if (fd.calculated_expression) {
        q.expression = fd.calculated_expression;
      }
      break;
    default:
      q.type = 'text';
  }
  return q;
}

/**
 * Full month names for a RENT_HISTORY row's month dropdown. The choice **value** is the 0-indexed
 * month (0 = January), matching the backend `PastDueRentHistory.month` and the pdrh_* document tokens.
 */
export const RENT_HISTORY_MONTH_CHOICES: { value: number; text: string }[] = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
].map((text, value) => ({value, text}));

/**
 * Builds the SurveyJS `paneldynamic` for a RENT_HISTORY field: a dynamic list of up to 10
 * {month, year, amount} rows. Persisted shape: `data[key] = [{month, year, amount}, ...]`.
 */
export function rentHistorySurveyQuestion(fd: FieldDefinition): Record<string, any> {
  const currentYear = new Date().getFullYear();
  const yearChoices = Array.from({length: 8}, (_, i) => currentYear - i);
  return {
    type: 'paneldynamic',
    name: fd.key,
    title: fd.label || fd.key,
    description: fd.help_text || undefined,
    renderMode: 'list',
    panelCount: fd.required ? 1 : 0,
    minPanelCount: fd.required ? 1 : 0,
    maxPanelCount: 10,
    confirmDelete: true,
    panelAddText: 'Add month',
    panelRemoveText: 'Remove',
    templateElements: [
      {type: 'dropdown', name: 'month', title: 'Month', isRequired: true, choices: RENT_HISTORY_MONTH_CHOICES},
      {type: 'dropdown', name: 'year', title: 'Year', isRequired: true, startWithNewLine: false, choices: yearChoices},
      {type: 'text', name: 'amount', title: 'Amount', inputType: 'number', isRequired: true, startWithNewLine: false}
    ]
  };
}

/** True when a SurveyJS paneldynamic JSON element is a RENT_HISTORY list (month/year/amount rows). */
export function isRentHistorySurveyElement(el: any): boolean {
  if (el?.type !== 'paneldynamic') {
    return false;
  }
  const names = (el.templateElements || []).map((e: any) => e?.name);
  return names.includes('month') && names.includes('year') && names.includes('amount');
}

const SURVEY_TYPE_TO_FIELD_TYPE: Record<string, CustomFieldType> = {
  text: CustomFieldType.TEXT,
  comment: CustomFieldType.MULTI_TEXT,
  dropdown: CustomFieldType.SELECT,
  tagbox: CustomFieldType.TAGS,
  radiogroup: CustomFieldType.RADIO,
  // SurveyJS "checkbox" is a multi-choice group — what MULTI_SELECT emits — not a long text box.
  checkbox: CustomFieldType.MULTI_SELECT,
  boolean: CustomFieldType.BOOLEAN,
  datepicker: CustomFieldType.DATE,
  file: CustomFieldType.FILE_UPLOAD
};

/**
 * The {value, label} pairs behind a SurveyJS checkbox question's choices, when they carry a display
 * text distinct from their value. Only MULTI_SELECT reads these; every other choice type keeps
 * label-equals-value (Req 2.6).
 */
function surveyOptionDefinitions(element: any): CustomFieldOption[] | undefined {
  const choices = element?.choices;
  if (!Array.isArray(choices) || !choices.length) {
    return undefined;
  }
  const pairs = choices.map((c: any) => (typeof c === 'object'
    ? {value: String(c.value ?? c.text), label: String(c.text ?? c.value)}
    : {value: String(c), label: String(c)}));
  // Where no choice carries display text distinct from its value, definitions would say exactly what
  // `options` already says. Req 2.2 reads absent definitions as label-equals-value, so returning
  // undefined is the same field with less payload — and keeps this function honest about its name.
  return pairs.some(p => p.label !== p.value) ? pairs : undefined;
}

function surveyChoices(element: any): string[] | undefined {
  const choices = element?.choices;
  if (!Array.isArray(choices)) {
    return undefined;
  }
  return choices.map((c: any) => (typeof c === 'object' ? (c.value ?? c.text) : c)).map((c: any) => String(c));
}

/**
 * Derives default {@link FieldDefinition}s from a SurveyJS intake form definition
 * — one per named question — so a legacy form-only template's Fields list mirrors
 * its intake form. Derived fields are marked built-in and non-removable.
 */
export function deriveFieldDefinitionsFromForm(formDefinitionJson?: string): FieldDefinition[] {
  if (!formDefinitionJson) {
    return [];
  }
  let parsed: any;
  try {
    parsed = JSON.parse(formDefinitionJson);
  } catch {
    return [];
  }
  const out: FieldDefinition[] = [];
  const seen = new Set<string>();
  const toField = (el: any): FieldDefinition => ({
    key: el.name,
    label: el.title || el.name,
    type: SURVEY_TYPE_TO_FIELD_TYPE[el.type] || CustomFieldType.TEXT,
    options: surveyChoices(el),
    ...(el?.type === 'checkbox' ? {option_definitions: surveyOptionDefinitions(el)} : {}),
    required: el.isRequired === true,
    built_in: true,
    removable: false
  });
  // A subject's nested sub-fields (one level; subjects can't nest subjects).
  const deriveSubFields = (templateElements: any[]): FieldDefinition[] => {
    const subs: FieldDefinition[] = [];
    const subSeen = new Set<string>();
    for (const el of templateElements || []) {
      if (!el?.name || subSeen.has(el.name)) {
        continue;
      }
      subSeen.add(el.name);
      subs.push(toField(el));
    }
    return subs;
  };
  const walk = (elements: any[]) => {
    for (const el of elements || []) {
      // A paneldynamic is a subject collection: one field carrying the nested schema.
      if (el?.type === 'paneldynamic') {
        if (!el?.name || seen.has(el.name)) {
          continue;
        }
        seen.add(el.name);
        // A {month, year, amount} paneldynamic is a RENT_HISTORY field, not a subject collection.
        if (isRentHistorySurveyElement(el)) {
          out.push({
            key: el.name,
            label: el.title || el.name,
            type: CustomFieldType.RENT_HISTORY,
            built_in: true,
            removable: false
          });
          continue;
        }
        out.push({
          key: el.name,
          label: el.title || el.name,
          type: CustomFieldType.ARRAY,
          subject_collection: true,
          subject_type: el.subjectType === 'ORGANIZATION' ? SubjectType.ORGANIZATION : SubjectType.INDIVIDUAL,
          subject_label_singular: el.subjectLabelSingular,
          subject_fields: deriveSubFields(el.templateElements),
          built_in: true,
          removable: false
        });
        continue;
      }
      if (Array.isArray(el?.elements)) {
        walk(el.elements); // panels / nested containers
        continue;
      }
      if (!el?.name || seen.has(el.name)) {
        continue;
      }
      seen.add(el.name);
      out.push(toField(el));
    }
  };
  for (const page of parsed?.pages || []) {
    walk(page?.elements);
  }
  return out;
}

/**
 * Default nested sub-field schema for a subject collection, by {@link SubjectType}.
 * Mirrors the backend `SubjectSchemas`. Seeds a new subject collection's
 * `subject_fields` (and its SurveyJS paneldynamic template).
 */
const INDIVIDUAL_SUBJECT_FIELDS: FieldDefinition[] = [
  {key: 'first_name', label: 'First Name', type: CustomFieldType.TEXT, built_in: true, required: true},
  {key: 'middle_name', label: 'Middle Name', type: CustomFieldType.TEXT, built_in: true},
  {key: 'last_name', label: 'Last Name', type: CustomFieldType.TEXT, built_in: true, required: true},
  {key: 'phone', label: 'Phone Number', type: CustomFieldType.TEXT, built_in: true},
  {key: 'email', label: 'Email', type: CustomFieldType.TEXT, built_in: true},
  {key: 'dob', label: 'Date of Birth', type: CustomFieldType.DATE, built_in: true},
  {key: 'ssn', label: 'SSN', type: CustomFieldType.TEXT, built_in: true}
];

const ORGANIZATION_SUBJECT_FIELDS: FieldDefinition[] = [
  {key: 'legal_name', label: 'Legal Name', type: CustomFieldType.TEXT, built_in: true, required: true},
  {key: 'dba', label: 'DBA / Trade Name', type: CustomFieldType.TEXT, built_in: true},
  {key: 'ein', label: 'EIN', type: CustomFieldType.TEXT, built_in: true},
  {key: 'registered_agent', label: 'Registered Agent', type: CustomFieldType.TEXT, built_in: true},
  {key: 'phone', label: 'Phone Number', type: CustomFieldType.TEXT, built_in: true},
  {key: 'email', label: 'Email', type: CustomFieldType.TEXT, built_in: true}
];

export function defaultSubjectFields(type: SubjectType): FieldDefinition[] {
  const src = type === SubjectType.ORGANIZATION ? ORGANIZATION_SUBJECT_FIELDS : INDIVIDUAL_SUBJECT_FIELDS;
  return src.map(f => ({...f}));
}

/**
 * Default sidebar layout for a case type, derived from the intake form's **pages**:
 * each page becomes a section (its title) holding that page's registry fields, in order.
 * Registry fields not on any page fall into an "Other" section. This mirrors how the
 * eviction intake groups fields (Tenant Information / Property Details / Eviction Reason).
 */
export function deriveSidebarFromForm(formDefinitionJson: string | undefined, registryKeys: string[]): SidebarProjection {
  const keySet = new Set(registryKeys);
  const placed = new Set<string>();
  const sections: SidebarSection[] = [];
  let parsed: any = null;
  try {
    parsed = formDefinitionJson ? JSON.parse(formDefinitionJson) : null;
  } catch {
    parsed = null;
  }
  const collect = (elements: any[], into: string[]) => {
    for (const el of elements || []) {
      const name = el?.name;
      if (el?.type === 'paneldynamic') {
        if (name && keySet.has(name) && !placed.has(name)) {
          into.push(name);
          placed.add(name);
        }
        continue;
      }
      if (Array.isArray(el?.elements)) {
        collect(el.elements, into);
        continue;
      }
      if (name && keySet.has(name) && !placed.has(name)) {
        into.push(name);
        placed.add(name);
      }
    }
  };
  for (const page of parsed?.pages || []) {
    const keys: string[] = [];
    collect(page?.elements, keys);
    if (keys.length) {
      sections.push({title: page.title || page.name || 'Section', collapsed: false, field_keys: keys});
    }
  }
  // Registry fields not on any page stay **unclaimed** (a separate palette), not forced
  // into a section — see unplacedKeys().
  return {sections};
}

/**
 * Reconcile an existing sidebar layout against the current registry: drop field keys
 * that no longer exist and de-dupe. Fields not in any section are left **unclaimed**
 * (see {@link unplacedKeys}); they are never auto-shoved into a section.
 */
export function reconcileSidebar(projection: SidebarProjection | undefined, registryKeys: string[]): SidebarProjection {
  const keySet = new Set(registryKeys);
  const placed = new Set<string>();
  const sections: SidebarSection[] = (projection?.sections || []).map(s => ({
    ...s,
    field_keys: (s.field_keys || []).filter(k => {
      if (keySet.has(k) && !placed.has(k)) {
        placed.add(k);
        return true;
      }
      return false;
    })
  }));
  return {sections};
}

/** Registry field keys not placed in any sidebar section (the "unclaimed" palette). */
export function unplacedKeys(projection: SidebarProjection | undefined, registryKeys: string[]): string[] {
  const placed = new Set<string>();
  for (const s of projection?.sections || []) {
    for (const k of s.field_keys || []) {
      placed.add(k);
    }
  }
  return registryKeys.filter(k => !placed.has(k));
}

/**
 * Registry-first: GENERATE a SurveyJS `formDefinition` from the registry + intake projection — the
 * inverse of {@link deriveFieldDefinitionsFromForm}. Each page's questions are produced by
 * {@link fieldDefinitionToSurveyQuestion} for the referenced field, with per-question intake overrides
 * (title / required / inputType / choices) applied. Fields absent from the projection are omitted from the
 * intake form (they remain registry fields — e.g. sidebar-only workflow fields). Mirrors the backend
 * `CaseFormGenerator`, so the generated form round-trips back to the registry.
 */
export function intakeProjectionToFormDefinition(
  registry: FieldDefinition[],
  intake: IntakeProjection | undefined
): string {
  const byKey = new Map<string, FieldDefinition>(registry.filter(fd => !!fd.key).map(fd => [fd.key, fd]));
  const seenPageNames = new Set<string>();
  // SurveyJS question names must be unique; a projection that repeats a field_key (within or across
  // pages) would emit duplicate names. Keep the first occurrence and drop the rest.
  const seenFieldKeys = new Set<string>();
  const pages = (intake?.pages || []).map((page, i) => {
    // SurveyJS page names must be unique. Prefer the explicit name, else the title slug, else an
    // index-based fallback — then de-dupe (e.g. multiple untitled or same-title pages, whose slugs
    // would otherwise collide) by suffixing the unique page index.
    let name = page.name || slugifyKey(page.title) || `page_${i + 1}`;
    if (seenPageNames.has(name)) {
      // Keep bumping the suffix until unique — the first suffix could itself collide with an
      // explicit page name (e.g. an authored `details_2`).
      let n = i + 1;
      while (seenPageNames.has(`${name}_${n}`)) {
        n++;
      }
      name = `${name}_${n}`;
    }
    seenPageNames.add(name);
    return {
      name,
      title: page.title,
      elements: (page.elements || [])
        .map(q => {
          const fd = byKey.get(q.field_key);
          if (!fd || seenFieldKeys.has(q.field_key)) {
            return null;
          }
          seenFieldKeys.add(q.field_key);
          const el = fieldDefinitionToSurveyQuestion(fd);
          // Apply an explicit title override (including '') when present; leave the field's default
          // title when the projection omits it. Mirrors the backend `CaseFormGenerator` (`q.title ?: label`).
          if (q.title !== undefined && q.title !== null) {
            el.title = q.title;
          }
          if (q.required !== undefined) {
            if (q.required) {
              el.isRequired = true;
            } else {
              delete el.isRequired;
            }
          }
          if (q.input_type) {
            el.inputType = q.input_type;
          }
          if (q.choices && q.choices.length) {
            el.choices = q.choices;
          }
          return el;
        })
        .filter((e): e is Record<string, any> => e !== null)
    };
  });
  return JSON.stringify({showProgressBar: 'top', progressBarType: 'pages', pages});
}

/**
 * Inverse of {@link intakeProjectionToFormDefinition}: derive the persisted intake projection from the
 * LIVE SurveyJS Creator form JSON so canvas edits (add / remove / reorder fields, page changes) round-trip.
 * The backend regenerates `form_definition` from `registry + intake_projection`, so emitting the loaded
 * (stale) projection on save would discard visual edits. Only questions whose `name` is a registry key are
 * projected (unknown questions are ignored; the server FieldRegistryValidator is the backstop); page
 * titles/names and question order are preserved.
 */
export function surveyFormToIntakeProjection(
  formJson: any,
  registryKeys: Set<string>
): IntakeProjection {
  const rawPages: any[] = Array.isArray(formJson?.pages) ? formJson.pages : [];
  const seenKeys = new Set<string>();
  const pages: IntakePage[] = rawPages.map(page => {
    const elements: IntakeQuestion[] = (Array.isArray(page?.elements) ? page.elements : [])
      .map((el: any): IntakeQuestion | null => {
        const key = el?.name;
        if (!key || !registryKeys.has(key) || seenKeys.has(key)) {
          return null;
        }
        seenKeys.add(key);
        return {field_key: key};
      })
      .filter((q: IntakeQuestion | null): q is IntakeQuestion => q !== null);
    return {name: page?.name, title: page?.title, elements};
  });
  return {pages};
}

/**
 * Registry-first sibling of {@link deriveSidebarFromForm}: derive a default sidebar layout from the
 * INTAKE PROJECTION instead of the SurveyJS form — one section per intake page that places at least one
 * registry key, in page order (pages with no registry-backed keys are skipped). Only keys that exist in
 * `registryKeys` are placed; the rest stay unclaimed (see {@link unplacedKeys}).
 */
export function deriveSidebarFromProjection(
  intake: IntakeProjection | undefined,
  registryKeys: string[]
): SidebarProjection {
  const keySet = new Set(registryKeys);
  const placed = new Set<string>();
  const sections: SidebarSection[] = [];
  for (const page of intake?.pages || []) {
    const keys: string[] = [];
    for (const q of page.elements || []) {
      const k = q.field_key;
      if (k && keySet.has(k) && !placed.has(k)) {
        keys.push(k);
        placed.add(k);
      }
    }
    if (keys.length) {
      sections.push({title: page.title || page.name || 'Section', collapsed: false, field_keys: keys});
    }
  }
  return {sections};
}

/** Lowercase slug for a page name derived from its title. */
function slugifyKey(input?: string): string {
  return (input || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/** A single intake question referencing a registry field by `field_key`. */
export interface IntakeQuestion {
  field_key: string;
  question_type?: string;
  input_type?: string;
  title?: string;
  required?: boolean;
  choices?: string[];
}

export interface IntakePage {
  name?: string;
  title?: string;
  elements: IntakeQuestion[];
}

/** Constrained SurveyJS-shaped view over a case type's field registry. */
export interface IntakeProjection {
  pages: IntakePage[];
}

export interface SidebarSection {
  title?: string;
  collapsed?: boolean;
  /** Ordered registry field keys placed in this section. */
  field_keys: string[];
  /**
   * Section kind. Absent/`'fields'` (default) = a normal field-layout section. `'documents'`
   * = a pinned system section that renders the case's aggregated document list (host-provided
   * template) instead of fields; it can be reordered but not removed, and holds no field keys.
   */
  kind?: 'fields' | 'documents';
}

/** Sidebar layout: sections of ordered registry field keys. */
export interface SidebarProjection {
  sections: SidebarSection[];
}

export interface CaseStepTask extends BaseTask {
  required?: boolean;
  remove_on_step_completion?: boolean;
}

/** Mirrors backend `StepProgressStatus` (serialized uppercase). */
export type CaseStepStatus = 'STARTED' | 'COMPLETED' | 'SKIPPED' | 'UNDONE';

/**
 * Per-step progress embedded on a {@link Case}, keyed by the immutable
 * `step_key`. Mirrors backend `CaseStepProgress`. Tier B (requires P1).
 */
export interface CaseStepProgress {
  step_key: string;
  status: CaseStepStatus;
  started_at?: string;
  completed_at?: string;
  actor?: string;
  step_data?: Record<string, any>;
  /** User-defined or auto-computed due date for this step (ISO-8601). */
  deadline?: string;
  /** Ids of the users this step is assigned to (explicit per-step assignees; rolled up to the case). */
  assigned_user_ids?: string[];
  /** True when written by a workflow migration (auto-skipped steps before the chosen start step). */
  migration?: boolean;
}

/**
 * A backend case-activity audit entry. Mirrors the `CaseActivity` domain
 * (requires P3 — `GET /api/v1/cases/{id}/activity`).
 */
export interface CaseActivity {
  id: string;
  case_id: string;
  activity_type: string;
  summary?: string;
  actor?: string;
  step_key?: string;
  subject_key?: string;
  workflow_id?: string;
  workflow_version?: number;
  detail?: Record<string, any>;
  occurred_at: string;
}

/**
 * The recognized case-activity item families rendered by the case Activity Log.
 * Mirrors the backend `CaseActivityType` values the requirements name. Entries
 * whose `activity_type` is not one of these are treated as unrecognized and are
 * dropped upstream rather than rendered. The family → icon map (single source of
 * truth for the per-family icon) lives in the case-management module.
 */
export type CaseActivityFamily =
  | 'MESSAGE'
  | 'NOTE_ADDED'
  | 'ATTACHMENT_ADDED'
  | 'STEP_STARTED'
  | 'STEP_COMPLETED'
  | 'STEP_SKIPPED'
  | 'TASK_ACTIVITY'
  | 'COURT_DATE'
  | 'CASE_OPENED'
  | 'CASE_CLOSED'
  | 'CASE_REOPENED'
  | 'DOCUMENT_GENERATED';

/**
 * A SCRA/PACER check result for a tenant subject on a case. Mirrors the
 * `CaseCheck` domain (requires P4 — `GET /api/v1/cases/{id}/checks`).
 */
export interface CaseCheck {
  id: string;
  case_id: string;
  subject_key: string;
  check_type: string;
  status: string;
  latest?: boolean;
  result?: Record<string, any>;
  requested_by?: string;
  run_at: string;
}

export interface CaseWorkflowStep {
  id: string;
  step: number;
  name: string;
  description?: string;
  icon?: string;
  optional?: boolean;
  /** Go/No-Go decision step — while active, the case detail surfaces the decision actions. */
  decision?: boolean;
  disabled?: boolean;
  // Tier B (P1): immutable step key used to correlate with step_progress and to
  // drive key-based step actions. Optional → legacy cases fall back to `step`.
  step_key?: string;
  // Tier B (P5): per-step assignee role surfaced from the workflow step.
  assignee_role?: string;
  survey_id?: string;
  survey_definition?: string;
  step_data: Record<string, any>;
  document_templates: string[];
  /**
   * The unified Document_Generation_List for this step, resolved by the backend's
   * `resolveStepsForDisplay` (late-bound via `liveStepConfig`, fallback to the version
   * snapshot). Lets the interactive Case step-completion control pre-populate the
   * override selector. Optional/additive — absent on legacy/non-document steps.
   */
  document_generation?: DocumentGenerationEntry[];
  assigned_users: string[];
  tasks: CaseStepTask[];
  date_started?: string;
  date_completed?: string;
  skipped?: boolean;
  expected_days?: number;
  email_on_completion?: boolean;
  comment?: string;
}

export interface CaseNote {
  id: string;
  text: string;
  created_by: string;
  created_by_name: string;
  created_date: string;
}

export enum CaseLinkType {
  RELATED = 'RELATED',
  SUPERSEDES = 'SUPERSEDES',
  SUPERSEDED_BY = 'SUPERSEDED_BY',
  COMPANION = 'COMPANION',
  CLONES = 'CLONES',
  CLONED_BY = 'CLONED_BY'
}

export const CASE_LINK_TYPE_LABELS: Record<CaseLinkType, string> = {
  [CaseLinkType.RELATED]: 'Related',
  [CaseLinkType.SUPERSEDES]: 'Supersedes',
  [CaseLinkType.SUPERSEDED_BY]: 'Superseded By',
  [CaseLinkType.COMPANION]: 'Companion',
  [CaseLinkType.CLONES]: 'Clones',
  [CaseLinkType.CLONED_BY]: 'Cloned By'
};

export const CASE_LINK_TYPE_DESCRIPTIONS: Record<CaseLinkType, string> = {
  [CaseLinkType.RELATED]: 'General association between cases',
  [CaseLinkType.SUPERSEDES]: 'This case replaces or continues from the target case',
  [CaseLinkType.SUPERSEDED_BY]: 'This case was replaced or continued by the target case',
  [CaseLinkType.COMPANION]: 'Cases running concurrently that are contextually related',
  [CaseLinkType.CLONES]: 'This case was created as a clone of the target case',
  [CaseLinkType.CLONED_BY]: 'The target case was created as a clone of this case'
};

/**
 * Link types a user can pick when manually linking cases/evictions. CLONES/CLONED_BY are
 * created automatically when a case is cloned and are intentionally excluded here.
 */
export const MANUAL_CASE_LINK_TYPES: CaseLinkType[] = [
  CaseLinkType.RELATED,
  CaseLinkType.SUPERSEDES,
  CaseLinkType.SUPERSEDED_BY,
  CaseLinkType.COMPANION
];

export interface LinkedCase {
  linked_case_id: string;
  case_id: string;
  link_type: CaseLinkType;
  linked_date: string;
  linked_by_id?: string;
  linked_by_name?: string;
}

export type CaseLinkAction = 'LINKED' | 'UNLINKED';

export interface LinkedCaseAuditEntry {
  linked_case_id: string;
  case_id: string;
  action: CaseLinkAction;
  link_type: CaseLinkType;
  actor_id?: string;
  actor_name?: string;
  timestamp: string;
}

/** A resolved tag for display: value plus the current org's vocabulary name/color. */
export interface CaseTag {
  value: string;
  name?: string;
  color?: string;
}

/** An organization participating on a case. */
export interface CaseParty {
  org_id: string;
  role: string;
  name?: string;
}

export interface Case extends Auditing {
  id: string;
  case_id: string;
  name: string;
  owner_id: string;
  template_id: string;
  template_name: string;
  property_id?: string;
  form_definition: string;
  data: Record<string, any>;
  /** Backend-computed values for smart (calculated/offset) registry fields; display only, never persisted. */
  computed?: Record<string, any>;
  status: CaseStatus;
  workflow_steps: CaseWorkflowStep[];
  current_step_name: string;
  current_step_number: number;
  current_step_start_date: string;
  // Key-based workflow reference + step progress. Optional so legacy
  // cases fall back to current_step_number/current_step_name.
  workflow_id?: string;
  current_step_key?: string;
  step_progress?: CaseStepProgress[];
  /**
   * Denormalized court-date read-model scalars (Stage 0.5 Wave 3), written through from the case's
   * `COURT_DATE` events. Display-only here (the list sorts on `next_court_date`); the source of truth is
   * the court-date events surfaced via the court-dates panel.
   */
  next_court_date?: string;
  last_court_date?: string;
  last_court_result?: string;
  /** Denormalized links to the reusable tenant-directory entries (S0.1); used to open the tenant history (Wave 9). */
  tenant_record_ids?: string[];
  /** The current org's tags, resolved against its "case" vocabulary (viewer-resolved). */
  tags: CaseTag[];
  /** Org parties on the case (owner + others). */
  parties?: CaseParty[];
  attachments: Attachment[];
  notes: CaseNote[];
  location_ids: string[];
  assigned_user_ids: string[];
  open_date: string;
  closed_date?: string;
  batch_id?: string;
  linked_cases?: LinkedCase[];
  linked_case_audit?: LinkedCaseAuditEntry[];
  /** Stage 1/2: the attorney handoff, when this case has been submitted. Absent for standalone cases. */
  handoff?: CaseHandoff;
}

/**
 * Stage 1/2: the thin PM→attorney handoff record surfaced on a case. `resulting_eviction_case_id` (set at
 * ACCEPTED) backs the live-status read + one-click deep-link to the attorney eviction.
 */
export interface CaseHandoff {
  id: string;
  source_case_id: string;
  source_case_display_id?: string;
  attorney_org_id?: string;
  property_id?: string;
  resulting_eviction_case_id?: string;
  status: 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'CANCELLED';
  submitted_at?: string;
  decided_at?: string;
  rejection_reason?: string;
}

/** A connected attorney the PM may submit a case to (the submit picker). */
export interface AttorneyConnection {
  attorney_org_id: string;
  attorney_name?: string;
}

/**
 * Result of `POST /cases/{id}/escalate` (Start Self-Managed Eviction): the closed delinquency
 * case plus the spawned eviction case's ids, for direct navigation to the successor.
 */
export interface EscalateCaseResult {
  case: Case;
  spawned_case_id: string;
  spawned_case_display_id?: string;
}

/** A source field that carries over automatically at escalation: identical key or saved template. */
export interface EscalationMappingEntry {
  source_key: string;
  source_label?: string;
  target_key: string;
  target_label?: string;
  origin: 'exact' | 'template';
}

/** An AI-suggested mapping for a source field with no automatic target (user confirms). */
export interface EscalationMappingSuggestion {
  source_key: string;
  source_label?: string;
  target_key: string;
  target_label?: string;
  confidence: number;
}

/** A bare field reference (unmapped source field, or a target-required field starting empty). */
export interface EscalationMappingField {
  key: string;
  label?: string;
}

/** The escalation wizard's data-mapping preview (`GET /cases/{id}/escalation-mapping`). */
export interface EscalationMappingPreview {
  mapped: EscalationMappingEntry[];
  suggestions: EscalationMappingSuggestion[];
  unmapped: EscalationMappingField[];
  missing_required: EscalationMappingField[];
  template_id?: string;
  escalation_scope?: string;
}

/** Tiered plaintiff suggestion offered to the attorney at accept (always overridable). */
export interface PlaintiffSuggestion {
  client_id?: string;
  client_name?: string;
  /** 'learned' = property→plaintiff map (authoritative for property-linked intakes); 'name' = company-name match. */
  source: 'learned' | 'name' | 'none';
}

export interface CaseWorkflowStepTemplate {
  id: string;
  step: number;
  name: string;
  description?: string;
  icon?: string;
  optional?: boolean;
  /** Go/No-Go decision step (options-level; live-editable on in-use workflows). */
  decision?: boolean;
  survey_id?: string;
  /** @deprecated Legacy id list, dead on the PM path — not read or written. Use {@link document_generation}. */
  document_templates?: string[];
  /** Unified per-document config: template id + hide-from-client + optional conditional rule. */
  document_generation?: DocumentGenerationEntry[];
  expected_days?: number;
  email_on_completion?: boolean;
  assign_user_ids: string[];
  // Tier B (P5): per-step assignee role. Optional/additive.
  step_key?: string;
  assignee_role?: string;
}

/**
 * A step's unified document-generation entries as fresh, deeply-cloned copies (callers mutate
 * freely without touching the original step config — including the nested `conditional_rule`,
 * its conditions, and each condition's flat `source_field` and (possibly array) `value`).
 * `document_generation` is the sole source of step documents on the PM path — the legacy
 * `document_templates` id list is dead and deliberately ignored.
 */
export function toDocumentGenerationEntries(
  step: { document_generation?: DocumentGenerationEntry[] } | null | undefined
): DocumentGenerationEntry[] {
  return (step?.document_generation ?? []).map(e => ({
    ...e,
    conditional_rule: e.conditional_rule
      ? {
        conditions: (e.conditional_rule.conditions ?? []).map(c => ({
          ...c,
          source_field: c.source_field ? {...c.source_field} : c.source_field,
          value: Array.isArray(c.value) ? [...c.value] : c.value
        }))
      }
      : e.conditional_rule
  }));
}

/**
 * A named workflow within a {@link CaseTemplate}. A case type may define several
 * (one default). Independent of the attorney `WorkflowVersion` model — no
 * email/billable/court settings.
 */
export interface CaseWorkflow {
  id: string;
  name: string;
  is_default: boolean;
  steps: CaseWorkflowStepTemplate[];
}

export interface ReportFieldMapping {
  survey_field_name: string;
  display_label: string;
  field_type: string;
  include_in_grid: boolean;
  include_in_report: boolean;
}

/** A terminal business outcome a case of a given type can be closed with (S0-R1). */
export interface CaseDisposition {
  value: string;
  label: string;
  closes_case?: boolean;
  action?: string;
  counts_in_resolution_rate?: boolean;
  /** Requires a non-blank close reason note when chosen (server-enforced in closeCase). */
  requires_note?: boolean;
}

/** Non-binding tenant strike-hold suggestion (S0.3). */
export interface StrikeSuggestion {
  message: string;
  severity: string;
  suggested_hold_days: number;
  tenant_record_id: string;
  strike_count: number;
}

/** Org-configurable tenant recidivism / strike-hold rule (S0.3). */
export interface TenantStrikeRule {
  enabled: boolean;
  strike_threshold: number;
  window_days: number;
  hold_days: number;
}

/** Per-property delinquency rollup (S0.2). */
export interface PropertyAnalyticsRow {
  property_id: string;
  case_count: number;
  sum_past_due_rent: number;
  escalation_rate: number;
  cure_rate: number;
  recidivist_tenant_count: number;
}

/** Portfolio delinquency funnel (S0.2). */
export interface PortfolioFunnel {
  active: number;
  cured: number;
  escalated: number;
  submitted: number;
  dollars_at_risk: number;
}

/** Full per-property dashboard (Stage 3, S3-R1.3). */
export interface PropertyDashboard {
  property_id: string;
  name?: string;
  address?: string;
  owner_name?: string;
  unit_count: number;
  analytics?: PropertyAnalyticsRow;
  status_breakdown: Record<string, number>;
  submitted_to_attorney_count: number;
  cases: PropertyDashboardCase[];
}

export interface PropertyDashboardCase {
  id: string;
  case_id: string;
  name: string;
  status: string;
  current_step_name?: string;
  disposition?: string;
  open_date?: string;
  handoff_status?: string;
  resulting_eviction_case_id?: string;
}

export interface CaseTemplate extends Auditing {
  id: string;
  name: string;
  description?: string;
  organization_id: string;
  status: PublicationStatus;
  form_definition: string;
  field_registry?: FieldDefinition[];
  intake_projection?: IntakeProjection;
  sidebar_projection?: SidebarProjection;
  workflow_steps: CaseWorkflowStepTemplate[];
  document_template_ids: string[];
  case_id_prefix?: string;
  case_id_sequence: number;
  report_field_mappings: ReportFieldMapping[];
  name_template?: string;
  /** Named workflows for this case type (multiple per type, one default). */
  workflows?: CaseWorkflow[];
  /**
   * Configured close-reason categories offered when closing a case of this type
   * (Req 6.1, 6.4). When empty/absent the platform defaults are used.
   */
  close_reason_categories?: string[];
  /**
   * Terminal business outcomes offered when closing a case of this type (S0-R1). When non-empty a
   * disposition is required at close; when absent the case closes with a category only.
   */
  dispositions?: CaseDisposition[];
  /** True for admin-owned, org-agnostic shared catalog templates. */
  shared?: boolean;
  /** Set on an org adoption — the shared template it inherits built-in fields from. */
  parent_template_id?: string;
}

export interface CaseFilterParams {
  status?: CaseStatus;
  template_id?: string;
  search?: string;
  tags?: string[];
  /** Filter to cases linked to this property (S0.1). */
  property_id?: string;
  page?: number;
  size?: number;
}

export interface CaseReportFilter {
  start_date?: string;
  end_date?: string;
  status?: CaseStatus;
  location_ids?: string[];
}

export interface ImportCreateCasesDTO {
  file_id: string;
  template_id: string;
  field_mappings: Record<string, string>;
  /** Wave 5: undefined = create every row; 'update' = upsert the matching open case; 'skip' = skip duplicates. */
  dedupe_mode?: 'update' | 'skip';
}

export interface ImportSubmitDTO {
  file_id: string;
  field_mappings: Record<string, string>;
}
